import React, { useState, useEffect, useCallback } from 'react';

interface TextEditorModalProps {
  isOpen: boolean;
  title: string;
  value: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}

const TextEditorModal: React.FC<TextEditorModalProps> = ({
  isOpen,
  title,
  value,
  onSave,
  onCancel,
}) => {
  const [text, setText] = useState(value);
  const [fontSize, setFontSize] = useState(16);

  // Update text when value prop changes
  useEffect(() => {
    setText(value);
  }, [value, isOpen]);

  const handleSave = () => {
    // Trim whitespace and replace multiple line feeds with single line feeds
    const processed = text
      .trim()
      .replace(/\r\n/g, '\n') // Normalize Windows line endings
      .replace(/\n{2,}/g, '\n'); // Replace multiple line feeds with single
    
    onSave(processed);
  };

  const handleReformat = () => {
    // Split by lines to preserve line structure
    const lines = text.split('\n').map(line => {
      // For each line, replace multiple spaces with single space
      return line.replace(/  +/g, ' ').trim();
    }).filter(line => line.length > 0); // Remove empty lines
    
    // Join back with single line feeds, preserving bullet points and structure
    const reformatted = lines.join('\n').trim();
    setText(reformatted);
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 2, 24));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 2, 12));
  };

  const handleCancel = useCallback(() => {
    setText(value); // Reset to original value
    onCancel();
  }, [value, onCancel]);

  // Handle Escape key
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content text-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <button 
              className="btn btn-secondary"
              onClick={decreaseFontSize}
              title="Decrease text size"
              style={{ padding: '8px 14px', fontSize: '14px', minWidth: '44px', backgroundColor: '#ee0000', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1' }}
            >
              −
            </button>
            <span style={{ fontSize: '14px', color: '#333', minWidth: '45px', textAlign: 'center', fontWeight: '600' }}>
              {fontSize}px
            </span>
            <button 
              className="btn btn-secondary"
              onClick={increaseFontSize}
              title="Increase text size"
              style={{ padding: '8px 14px', fontSize: '14px', minWidth: '44px', backgroundColor: '#ee0000', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: '1' }}
            >
              +
            </button>
            <div style={{ borderLeft: '1px solid #ddd', height: '20px', margin: '0 4px' }}></div>
            <button 
              className="btn btn-secondary"
              onClick={handleReformat}
              title="Remove extra spaces, fix line breaks, and clean up formatting"
              style={{ padding: '8px 16px', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap', flex: 1, backgroundColor: '#ee0000', color: 'white' }}
            >
              Clean writeup
            </button>
            <button className="modal-close" onClick={handleCancel}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="text-editor-textarea"
            placeholder="Enter formatted text here. You can use multiple lines."
            autoFocus
            style={{ fontSize: `${fontSize}px` }}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextEditorModal;
